import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type {
    BimWorkOrderPriceData,
    BimWorkOrderTotalItemData,
    BimChapterEnum,
    BimWorkOrderVersionPriceRowData,
    BimPriceLevelEnum,
} from 'gql/graphql';
import { BimWorkOrderRowDetailsType, BimWorkOrderRowTypeEnum } from 'pages/Project/BimProject/WorkOrder/types';
import { useBimWorkOrderVersionTotalsRow } from 'pages/Project/BimProject/WorkOrderVersion/BimWorkOrderPriceVersionTable/hooks/useBimWorkOrderVersionTotalsRow';
import { bimWorkOrderVersionPriceRowsGql } from 'pages/Project/BimProject/WorkOrderVersion/workOrderVersionQueries';

import { useEffect, useMemo } from 'react';

import type { IBimWorkOrderRowDetails } from '../cellRenderers';

export const useBimWorkOrderPriceVersionDetails = ({
    checkedPriceRowForDetails,
    chapter,
    constructionTypeId,
    workOrderObjectId,
    workOrderVersionId,
    totalChapterDetailsRowOpenOuter,
    regularDetailsFilterValues,
    priceLevel,
}: {
    totalChapterDetailsRowOpenOuter: { rowType: BimWorkOrderRowTypeEnum.TotalsString; data: null } | null;
    checkedPriceRowForDetails: IBimWorkOrderRowDetails<BimWorkOrderTotalItemData | BimWorkOrderPriceData | null> | null;
    constructionTypeId: number | undefined;
    chapter: BimChapterEnum | undefined;
    workOrderObjectId: number | undefined;
    regularDetailsFilterValues: Record<string, string[]>;
    workOrderVersionId?: number;
    priceLevel: BimPriceLevelEnum | undefined;
}) => {
    const { rowType, data, detailsType } = checkedPriceRowForDetails || {};

    const detailsVersionVars = useMemo(() => {
        if (data && 'id' in data) {
            return { workOrderVersionPriceId: data?.id, priceLevel };
        }

        return undefined;
    }, [data, priceLevel]);

    const isRequestLegit = Boolean(
        !!checkedPriceRowForDetails &&
            detailsVersionVars?.workOrderVersionPriceId &&
            rowType === BimWorkOrderRowTypeEnum.RegularString &&
            detailsType === BimWorkOrderRowDetailsType.Details,
    );

    /*
     * данные для состава сметы
     * */

    const [detailsVersionData, detailsVersionStatus] = useRequest({
        requestId: 'workOrderVersionPriceRowDetails',
        query: bimWorkOrderVersionPriceRowsGql,
        withEffect: isRequestLegit,
        variables: detailsVersionVars,
    });

    const memoizedDetailsRequestIds = useMemo(
        () => ['bimWorkOrderPriceRowCodeFilter', 'bimWorkOrderPriceRowNameFilter', 'workOrderVersionPriceRowDetails'],
        [],
    );

    const [resetDetailsRows] = useResetMultipleStoreData({
        requestIds: memoizedDetailsRequestIds,
    });

    useEffect(() => {
        if (!checkedPriceRowForDetails) {
            resetDetailsRows();
        }
    }, [checkedPriceRowForDetails, resetDetailsRows]);

    const isRegularDetailsLoading = detailsVersionStatus === RequestStatus.FETCHING;

    const regularDetailsRows = useMemo(() => {
        const mappedVersion =
            detailsVersionData?.bimWorkOrderVersionPriceRows?.items?.reduce(
                (
                    acc: {
                        [key: number]: {
                            value: { name: string | undefined | null; isGroupTitle: boolean };
                            children: BimWorkOrderVersionPriceRowData[];
                        };
                    },
                    row,
                ) => {
                    if (!acc[row.sectionId]) {
                        acc[row.sectionId] = {
                            value: { name: row.sectionName, isGroupTitle: true },
                            children: [row],
                        };
                    } else {
                        acc[row.sectionId].children.push(row);
                    }

                    return acc;
                },
                {},
            ) ?? {};

        return Object.values(mappedVersion).reduce((acc: any[], row) => {
            acc.push({ planValue: row.value });
            if (row.children.length) {
                row.children.forEach((item) => {
                    let isPassedFilters = true;

                    if (regularDetailsFilterValues.code?.length && item?.value?.code) {
                        isPassedFilters = regularDetailsFilterValues.code.includes(item.value.code);
                    }

                    if (isPassedFilters && regularDetailsFilterValues.name?.length && item?.value?.name) {
                        isPassedFilters = regularDetailsFilterValues.name.includes(item.value.name);
                    }

                    if (isPassedFilters) {
                        acc.push({ ...item, planValue: item.value });
                    }
                });
            }

            return acc;
        }, []);
    }, [
        detailsVersionData?.bimWorkOrderVersionPriceRows?.items,
        regularDetailsFilterValues.code,
        regularDetailsFilterValues.name,
    ]);

    /*
     * данные для деталей итогов
     * */

    const { rowTotalsData, isLoading: isTotalsLoading } = useBimWorkOrderVersionTotalsRow({
        chapter,
        constructionTypeId,
        workOrderVersionId,
        workOrderObjectId,
        useInPrices: false,
        checkedPriceRowForDetails: checkedPriceRowForDetails || totalChapterDetailsRowOpenOuter,
        priceLevel,
    });

    const detailsBaseRow = useMemo(
        () =>
            (checkedPriceRowForDetails || totalChapterDetailsRowOpenOuter)?.rowType ===
            BimWorkOrderRowTypeEnum.TotalsString
                ? []
                : [{ ...checkedPriceRowForDetails?.data, topPinnedRow: true }],
        [checkedPriceRowForDetails, totalChapterDetailsRowOpenOuter],
    );

    const detailsRows = useMemo(
        () =>
            (checkedPriceRowForDetails || totalChapterDetailsRowOpenOuter)?.rowType ===
            BimWorkOrderRowTypeEnum.TotalsString
                ? rowTotalsData
                : regularDetailsRows,
        [checkedPriceRowForDetails, regularDetailsRows, rowTotalsData, totalChapterDetailsRowOpenOuter],
    );

    const isDetailsLoading = useMemo(
        () =>
            (checkedPriceRowForDetails || totalChapterDetailsRowOpenOuter)?.rowType ===
            BimWorkOrderRowTypeEnum.TotalsString
                ? isTotalsLoading
                : isRegularDetailsLoading,
        [checkedPriceRowForDetails, isRegularDetailsLoading, isTotalsLoading, totalChapterDetailsRowOpenOuter],
    );

    return {
        detailsData: detailsRows ?? [],
        detailsBaseRow,
        isDetailsLoading,
    };
};
